import { LngLatBounds } from "maplibre-gl";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import Map, { Layer, MapRef, NavigationControl, ScaleControl, Source } from "react-map-gl/maplibre";
import { emptyFeatureCollection } from "../../util/geo";
import { parcelLineStyle, projectFillStyle, projectStyle } from "../map/layerStyles";
import { PARCEL_SOURCE_LAYER, PARCEL_TILES } from "../map/pmtiles";
import { fetchFilterMeta, fetchSearchHits } from "../map/searchHits";
import { applyMatches, searchDotsLayer, searchMatchLayer } from "../map/searchResultLayers";
import { boundsForList } from "../map/util";
import { CaMapContext, useCaMap, useCaMapEvents } from "../state";
import { FIT_PADDING, fitToParcels } from "./fit";
import { useWorkspaceKavel } from "./kavel";
import { useWorkspaceSearch } from "./search";

// De transparante perceellaag maakt ook kavels buiten het actieve filter en
// project aanklikbaar, net als de perceellaag op de globale kaart.
const INTERACTIVE_LAYERS = ["workspaceHits", "parcelMatch", "projectsFill", "parcel"];

// Heel Nederland; startpunt voor een project zonder percelen.
const NL_VIEW = { longitude: 5.3, latitude: 52.15, zoom: 6.5 };

export const WorkspaceMap = ({ parcels }: { parcels: any[] }) => {
  const setMap = useContext(CaMapContext)[1];
  const map = useCaMap();
  const [bounds] = useCaMapEvents("moveend");
  const { client, params, active, appliedAt, setTotal, setError } = useWorkspaceSearch();
  const selectKavel = useWorkspaceKavel((s) => s.select);
  const selectedParcelName = useWorkspaceKavel((s) => s.cube?.name);

  // De hits als React-state, niet via source.setData(): die bron bestaat pas na
  // de eerste render van <Source>, en dan zou een effect dat te vroeg draait
  // nooit meer langskomen.
  const [hits, setHits] = useState(emptyFeatureCollection);
  // Namen van de gevonden percelen; hiermee lichten we ze op in de vector-tiles,
  // want daar is `id` de perceelnaam (feature-state, zie searchResultLayers).
  const [names, setNames] = useState<string[]>([]);
  const matched = useRef(new Set<string>());
  useEffect(() => {
    if (!map?.getSource("parcels")) return;
    matched.current = applyMatches(map, "parcels", matched.current, names);
  }, [map, names]);
  const fetchedKey = useRef<string | null>(null);
  const hoveredParcelId = useRef<string | number | null>(null);

  // Zoomen hangt aan het tóépassen van een filter, niet aan het pannen: anders
  // zou de kaart je bij elke verschuiving terugtrekken. En het gebeurt altijd,
  // ook als er al een hoekje van de selectie in beeld staat.
  //
  // `apply()` wist de zoekparameters; deze effect wacht dus tot ze er weer zijn
  // en hóren bij het net aangeklikte filter. Zonder die volgorde zoomt de kaart
  // naar de vorige selectie — of, bij het eerste filter, naar heel Nederland.
  const zoomedFor = useRef(0);
  useEffect(() => {
    if (!map || !active || !params || !appliedAt) return;
    if (zoomedFor.current === appliedAt) return;
    zoomedFor.current = appliedAt;

    let cancelled = false;
    // Eén query levert zowel het aantal treffers van het hele filter als de
    // omvang ervan. Het aantal in beeld (hieronder) is een ander getal.
    fetchFilterMeta(client, params).then(({ total, bounds: filterBounds }) => {
      if (cancelled) return;
      setTotal(total);
      if (filterBounds && !filterBounds.isEmpty()) {
        map.fitBounds(filterBounds, { padding: FIT_PADDING, maxZoom: 15 });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [map, client, active, params, appliedAt, setTotal]);

  useEffect(() => {
    if (!active || !params) {
      setHits(emptyFeatureCollection);
      setNames([]);
      fetchedKey.current = null;
      return;
    }
    if (!map) return;

    const mapBounds = (bounds ?? map.getBounds()) as LngLatBounds;
    const key = JSON.stringify([params, mapBounds.toArray()]);
    if (key === fetchedKey.current) return;
    fetchedKey.current = key;

    let cancelled = false;
    // `boundsToFit` bewust genegeerd: dat is de fallback van /search die naar de
    // treffers toe zoomt zodra er niets in beeld staat. Hier zoomen we al bij
    // het toepassen van een filter, en verderop kijken moet gewoon kunnen.
    fetchSearchHits(client, params, mapBounds)
      .then(({ geojson, names: hitNames }) => {
        if (cancelled) return;
        setHits(geojson);
        setNames(hitNames);
      })
      .catch((e) => {
        if (cancelled) return;
        console.error("Kavels ophalen mislukt", e);
        setError("Kavels ophalen mislukt");
        fetchedKey.current = null;
      });
    return () => {
      cancelled = true;
    };
  }, [map, client, params, active, bounds, setError]);

  // De percelen komen binnen met de projecten-fetch, dus mogelijk later dan de
  // kaart. Eén keer inzoomen zodra beide er zijn — en daarna niet meer, want
  // een perceel toevoegen mag je beeld niet verspringen.
  const fitted = useRef(false);
  useEffect(() => {
    if (fitted.current) return;
    fitted.current = fitToParcels(map, parcels);
  }, [map, parcels]);

  const initialViewState = useMemo(() => {
    if (!parcels.length) return NL_VIEW;
    const b = boundsForList(parcels);
    if (b.isEmpty()) return NL_VIEW;
    const center = b.getCenter();
    return { longitude: center.lng, latitude: center.lat, zoom: 13 };
  }, [parcels]);

  const projectData = useMemo(
    () => ({
      type: "FeatureCollection" as const,
      features: parcels.map((parcel) => ({
        type: "Feature" as const,
        geometry: parcel.geometry,
        properties: { id: parcel.id, name: parcel.name, selected: true },
      })),
    }),
    [parcels]
  );

  const refCallback = useCallback(
    (ref: MapRef | null) => {
      // Bewust geen window["map"]: die hoort bij de globale kaart, waar
      // map/viewstate.ts de viewstate uit wegschrijft.
      if (ref) setMap(ref.getMap() as any);
    },
    [setMap]
  );

  const clearHover = useCallback((target: any) => {
    if (hoveredParcelId.current == null) return;
    target.setFeatureState(
      {
        source: "parcels",
        sourceLayer: PARCEL_SOURCE_LAYER,
        id: hoveredParcelId.current,
      },
      { hover: false }
    );
    hoveredParcelId.current = null;
  }, []);

  // Klik op een kavel — als stip, als gefilterd vlak of als perceel van het
  // project: detail in dit paneel, géén navigatie naar /location zoals de
  // globale kaart doet. Bij een vlak is de klikpositie het aanknopingspunt; de
  // cube-lookup werkt op elk punt binnen het perceel.
  const onClick = useCallback(
    (e: any) => {
      const feature = e.features?.[0];
      if (!feature) return;
      const point =
        feature.geometry?.type === "Point"
          ? feature.geometry.coordinates
          : [e.lngLat.lng, e.lngLat.lat];
      clearHover(e.target);
      selectKavel([point[0], point[1]]);
    },
    [clearHover, selectKavel]
  );

  const onMouseMove = useCallback(
    (e: any) => {
      // Een projectvlak of zoekstip kan boven de basislaag liggen en daardoor
      // als eerste interactieve feature terugkomen. Vraag de perceellaag
      // expliciet op, zodat hover voor ieder zichtbaar perceel hetzelfde werkt.
      const parcel = e.target.queryRenderedFeatures(e.point, { layers: ["parcel"] })[0];
      const nextId = parcel?.id ?? null;
      e.target.getCanvas().style.cursor = nextId != null ? "pointer" : "grab";
      if (nextId === hoveredParcelId.current) return;

      clearHover(e.target);
      if (nextId != null) {
        e.target.setFeatureState(
          { source: "parcels", sourceLayer: PARCEL_SOURCE_LAYER, id: nextId },
          { hover: true }
        );
        hoveredParcelId.current = nextId;
      }
    },
    [clearHover]
  );

  const onMouseLeave = useCallback(
    (e: any) => {
      clearHover(e.target);
      e.target.getCanvas().style.cursor = "grab";
    },
    [clearHover]
  );

  return (
    <Map
      initialViewState={initialViewState}
      mapStyle="https://tiles.openfreemap.org/styles/bright"
      ref={refCallback}
      style={{ width: "100%", height: "100%" }}
      interactiveLayerIds={INTERACTIVE_LAYERS}
      onClick={onClick}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
    >
      <NavigationControl position="top-right" />
      <ScaleControl />
      <Source id="parcels" type="vector" promoteId="id" url={PARCEL_TILES}>
        <Layer
          id="parcel"
          source-layer={PARCEL_SOURCE_LAYER}
          type="fill"
          paint={{ "fill-color": "rgba(0,0,0,0)" }}
        />
        <Layer
          id="parcelLine"
          source-layer={PARCEL_SOURCE_LAYER}
          type="line"
          {...parcelLineStyle}
        />
        {/* Zoekresultaten als percelen zodra ver genoeg is ingezoomd; daaronder
            de stippen in `workspaceHits`, zie searchResultLayers.ts. */}
        <Layer
          id="parcelMatch"
          source-layer={PARCEL_SOURCE_LAYER}
          type="fill"
          {...searchMatchLayer}
        />
        <Layer
          id="parcelHover"
          source-layer={PARCEL_SOURCE_LAYER}
          type="line"
          paint={{
            "line-color": "rgb(202, 138, 4)",
            "line-opacity": ["case", ["boolean", ["feature-state", "hover"], false], 1, 0],
            "line-width": 3,
          }}
        />
        <Layer
          id="parcelSelected"
          source-layer={PARCEL_SOURCE_LAYER}
          type="line"
          filter={["==", ["get", "id"], selectedParcelName ?? ""]}
          paint={{
            "line-color": "rgb(146, 64, 14)",
            "line-width": 4,
          }}
        />
      </Source>
      <Source id="projects" type="geojson" data={projectData}>
        <Layer id="projects" type="line" {...projectStyle} />
        <Layer id="projectsFill" type="fill" {...projectFillStyle} />
      </Source>
      <Source id="workspaceHits" type="geojson" data={hits}>
        <Layer id="workspaceHits" type="circle" {...searchDotsLayer} />
      </Source>
    </Map>
  );
};
